import * as React from 'react';
import { Component } from 'react';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import i18n from 'i18n';
import { ButtonGroup, Tab, Tabs } from 'react-bootstrap';
import { fetchInspectionViewRequestsThunk } from 'store/analytics/data/analyticsDataActions';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { selectRequestDetailTab } from 'store/analytics/ui/subViews/inspectionView/inspectionViewActions';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import { TxInspection, TxiViewRequest } from 'types/rest/analyticsTypes';
import BlazeTable from 'components/blazeTable/BlazeTable';
import Row from 'react-bootstrap/Row';
import OutdatedRequest from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/OutdatedRequest';
import InspectionDetailsHeader from 'views/Analytics/AnalyticsMockService/Inspection/Header/InspectionDetailsHeader';
import FullPageLoader from 'components/loaders/FullPageLoader';
import _ from 'lodash';
import ExpandableBody from '../../Common/ExpandableBody';

type AllProps = StateProps & DispatchProps;

class TransactionInspectionDetails extends Component<AllProps> {
  tableRef: any = React.createRef<BlazeTable>();

  render() {
    const getColumnsDefinition = (useType: boolean) => {
      // Definition of table columns
      const colsDefinition: BlazeTableColDef[] = [
        {
          header: i18n.t('analytics:txInspection.detail.name'),
          width: 30,
          accessor: 'key',
        },
        {
          header: i18n.t('analytics:txInspection.detail.value'),
          width: useType ? 40 : 70,
          accessor: 'value',
        },
      ];

      if (useType) {
        colsDefinition.push({
          header: i18n.t('analytics:txInspection.detail.type'),
          width: 30,
          accessor: 'type',
        });
      }
      return colsDefinition;
    };

    const getTableOptions = (useType: boolean) => {
      const tableOptions: BlazeTableOptions = {
        showRowExpander: false,
        columnDef: getColumnsDefinition(useType),
        pageable: true,
        selectable: false,
        pageSize: 5,
        minWidth: 950,
        minHeight: 150,
        tableName: 'eventList',
        noResultsText: i18n.t('analytics:txInspection.table.noResults'),
        customHeaderClassName: 'flex-table-item-top',
        customTableClassName: 'flex-table-simple flex-table-condensed',
        maxContentHeight: '185px',
      };

      return tableOptions;
    };

    //In case data & ui contexts are not ready, do not render the component
    if (_.isNil(this.props.activeDataContext) || _.isNil(this.props.activeUiContext)) {
      return null;
    }

    const uiContext = this.props.activeUiContext;
    const selectedTab = uiContext.inspectionView.selectedViewTab;
    const selectedLevel = uiContext.inspectionView.selectedLevelTab;
    const selectedRequest = uiContext.inspectionView.selectedRequest as TxiViewRequest;

    const tabId = this.props.activeDataContext.tabId;
    const inspectionState = this.props.activeDataContext.inspection;
    const isRequestOutdated = inspectionState ? inspectionState.isRequestOutdated : false;
    const txInspection = inspectionState.requestDetails as TxInspection;
    const isLoading = inspectionState ? inspectionState.isLoadingRequestDetails : false;

    let details = txInspection ? txInspection.request : null;
    if (selectedTab === 'secondTab') {
      details = txInspection ? txInspection.response : null;
    }

    return (
      <div className="mt-4 pt-4 position-relative" style={{ borderTop: '1px solid #c8d1e0', minHeight: '400px' }}>
        {isLoading && (
          <div className="blur-background">
            <FullPageLoader />
          </div>
        )}

        <div className={isLoading ? 'blur' : ''}>
          {/* Header */}
          {isRequestOutdated === false && (
            <div className="mb-2 mt-2" style={{ minHeight: 30 }}>
              <Row className="mt-3 mb-3">
                <Col lg={10} md={7} sm={6}>
                  <InspectionDetailsHeader
                    method={selectedRequest && selectedRequest.operation ? selectedRequest.operation : ''}
                    operation={selectedRequest && selectedRequest.url ? selectedRequest.url : ''}
                    seconds={true}
                    requests={selectedRequest}
                  />
                </Col>

                {/* Do not show Tabs if request detail is outdated */}
                <Col className={'text-right'} lg={2} md={5} sm={6}>
                  <ButtonGroup>
                    <Button
                      variant={selectedTab === 'defaultTab' ? 'primary' : 'secondary'}
                      onClick={() => {
                        this.props.selectRequestDetailTab('defaultTab');
                      }}
                    >
                      {i18n.t('analytics:txInspection.detail.requestDetails')}
                    </Button>
                    <Button
                      variant={selectedTab === 'secondTab' ? 'primary' : 'secondary'}
                      onClick={() => this.props.selectRequestDetailTab('secondTab')}
                    >
                      {i18n.t('analytics:txInspection.detail.responseDetails')}
                    </Button>
                  </ButtonGroup>
                </Col>
              </Row>
            </div>
          )}
          {/* Outdated Request Placeholder */}
          {isRequestOutdated === true && (
            <OutdatedRequest refreshRequestsHandler={() => this.props.fetchInspectionViewRequestsThunk(tabId)} />
          )}

          {/* Render body only when details are ready */}
          {!_.isNil(details) && !_.isNil(details.header) && isRequestOutdated === false && (
            <>
              {selectedTab === 'secondTab' && (
                <div className="mt-2 mb-2">
                  <label className="mb-3 mr-3">{i18n.t('analytics:txInspection.detail.statusCode')}:</label>
                  <span>{details.resultCode}</span>
                </div>
              )}
              <div className="position-relative">
                <Tabs
                  defaultActiveKey={selectedLevel}
                  activeKey={selectedLevel}
                  id="mock-service-analytics-tabs"
                  onSelect={() => _.noop()}
                >
                  <Tab eventKey="Header" title={'Headers (' + details.header.length + ')'}>
                    <BlazeTable
                      options={getTableOptions(false)}
                      data={details.header}
                      onRef={(ref: any) => (this.tableRef = ref)}
                      showPageSizeDropdown={true}
                    />
                  </Tab>
                  {!_.isNil(txInspection) &&
                    !_.isNil(txInspection.runnerType) &&
                    txInspection.runnerType === 'MESSAGING' && (
                      <Tab
                        eventKey="Properties"
                        title={'Properties (' + (!_.isNil(details.properties) ? details.properties.length : 0) + ')'}
                      >
                        <BlazeTable
                          options={getTableOptions(true)}
                          data={!_.isNil(details.properties) ? details.properties : []}
                          onRef={(ref: any) => (this.tableRef = ref)}
                          showPageSizeDropdown={true}
                        />
                      </Tab>
                    )}
                  <Tab eventKey="Body" title="Body">
                    <label className="d-inline-block mb-3">{i18n.t('analytics:txInspection.detail.bodyContent')}</label>
                    {details.bodyContent ? (
                      <div className={'position-relative'} style={{ bottom: '22px' }}>
                        <ExpandableBody expandedHeight={1000} height={360}>
                          {details.bodyContent}
                        </ExpandableBody>
                      </div>
                    ) : (
                      <p>{i18n.t('analytics:txInspection.detail.noBodyContentAvailable')}</p>
                    )}
                  </Tab>
                </Tabs>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ analytics }: ApplicationState) => ({
  activeUiContext: analytics.ui.activeUiContext,
  activeDataContext: analytics.data.activeDataContext,
});

const mapDispatchToProps = {
  selectRequestDetailTab,
  fetchInspectionViewRequestsThunk,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(TransactionInspectionDetails);
